import type { Experiment } from 'types';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from 'helpers/cn';
import { resolveWeights } from 'helpers/weight-distribution';
import { useSchedule } from '@/state/experiments/useSchedule';
import { useVariants } from '@/state/experiments/useVariants';
import { isPreStart } from 'helpers/experiment-status';
import { PlannedRunHint } from '../ExperimentPlan';

type ScheduleConfigProps = { experiment: Experiment };

const DAY = 24 * 60 * 60 * 1000;

/**
 * datetime-local reads and writes in the browser's own zone, so `new Date(value)`
 * resolves the wall-clock time the person typed into the absolute instant they meant.
 * That instant is what the server stores and the scheduler compares against, which is
 * why nothing downstream does any timezone arithmetic at all.
 */
const toInputValue = (epoch: number | null): string => {
  if (epoch === null) return '';
  const local = new Date(epoch - new Date(epoch).getTimezoneOffset() * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

const toEpoch = (value: string): number | null => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Colour is the state, not decoration: armed is the shipped intent, held and blocked are
 * both decisions waiting on someone, active is live, and a finished schedule is neutral.
 */
const STATE_LABELS: Record<string, { label: string; tone: string; dot: string }> = {
  armed: { label: 'scheduled', tone: 'text-accent', dot: 'bg-accent' },
  held: { label: 'held', tone: 'text-warn', dot: 'bg-warn' },
  active: { label: 'running to schedule', tone: 'text-ok', dot: 'bg-ok' },
  blocked: { label: 'needs attention', tone: 'text-danger', dot: 'bg-danger' },
  complete: { label: 'finished', tone: 'text-dim', dot: 'bg-bar-muted' },
  cancelled: { label: 'cancelled', tone: 'text-dim', dot: 'bg-bar-muted' },
};

/**
 * Split in two so the form's initial state comes from a schedule that has already
 * loaded. useState initialisers run once on mount, so a single component would show
 * empty fields for an experiment that does have a schedule, and silently save those
 * empty fields back. The key remounts the form if the schedule is replaced.
 */
export const ScheduleConfig = ({ experiment }: ScheduleConfigProps) => {
  const manager = useSchedule(experiment.id);
  if (manager.isLoading) return null;
  return <ScheduleForm key={manager.schedule?.id ?? 'new'} experiment={experiment} manager={manager} />;
};

type ScheduleFormProps = { experiment: Experiment; manager: ReturnType<typeof useSchedule> };

const ScheduleForm = ({ experiment, manager }: ScheduleFormProps) => {
  const { schedule, saveSchedule, removeSchedule } = manager;
  const { variants } = useVariants(experiment.id);
  const [isSaving, setIsSaving] = useState(false);

  // A scheduled start takes a draft in QA straight to Running.
  const isDraft = isPreStart(experiment.status);
  const isRunning = experiment.status === 'Running';

  const [startAt, setStartAt] = useState(toInputValue(schedule?.startAt ?? null));
  const [stopAt, setStopAt] = useState(toInputValue(schedule?.stopAt ?? null));
  const [stopOnSignificance, setStopOnSignificance] = useState(schedule?.stopOnSignificance ?? false);

  // Neither state can be scheduled: an ended or deployed experiment has nothing left
  // to start, and nothing running to stop.
  if (!isDraft && !isRunning) return null;

  const start = toEpoch(startAt);
  const needsWeights = isDraft && start !== null;

  // null both while variants are still loading and when the stored distribution
  // cannot be resolved (weights missing or not summing to 100%) — either way there
  // is nothing valid to snapshot yet, and submitting would send an empty or broken
  // variantWeights the API rejects with a message about a field this form never shows.
  const resolvedWeights = variants ? resolveWeights(variants) : null;
  const weightsUnavailable = needsWeights && !resolvedWeights;

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (weightsUnavailable) return;
    setIsSaving(true);

    await saveSchedule({
      startAt: isDraft ? start : null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      // Snapshotted with the schedule so a later weight edit cannot silently change
      // the split of a start that has already been approved.
      variantWeights: needsWeights ? resolvedWeights : null,
      stopAt: toEpoch(stopAt),
      stopOnSignificance,
      minRuntimeMs: null,
    });

    setIsSaving(false);
  };

  const onRemove = async () => {
    await removeSchedule();
    setStartAt('');
    setStopAt('');
    setStopOnSignificance(false);
  };

  const stateMeta = schedule ? STATE_LABELS[schedule.state] : null;

  return (
    <section className="bg-panel border-line rounded-panel border" data-tour="settings-schedule">
      <div className="border-line flex items-start justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <h2 className="micro-label text-foreground">Schedule</h2>
          <p className="supporting mt-1">
            {isDraft
              ? 'Start this experiment at a set time, and stop it at a set time or when the result is conclusive.'
              : 'Stop this experiment at a set time, or when the result is conclusive.'}
          </p>
        </div>
        {stateMeta && (
          <span className="flex shrink-0 items-center gap-2">
            <span className={cn('size-[5px] rounded-pill', stateMeta.dot)} />
            <span className={cn('font-mono text-nano tracking-[0.1em] lowercase', stateMeta.tone)}>
              {stateMeta.label}
            </span>
          </span>
        )}
      </div>

      <div className="p-4">
        {schedule?.blockedReason && (
          <p className="border-warn-line rounded-card supporting mb-4 border px-3 py-2">
            {schedule.state === 'held' ? 'Waiting: ' : 'Stopped acting: '}
            {schedule.blockedReason}
            {schedule.heldSince && <span> (since {new Date(schedule.heldSince).toLocaleDateString()})</span>}
          </p>
        )}

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          {/*
            Start and stop side by side: they are one decision — the window the experiment
            runs for — and stacking them read as two unrelated fields. On a draft both
            appear; on a running experiment only the stop, which keeps its column width
            rather than stretching across the card.
          */}
          <div className="grid gap-4 sm:max-w-xl sm:grid-cols-2">
            {isDraft && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="schedule-start" className="micro-label">
                  Start at
                </Label>
                <Input
                  id="schedule-start"
                  type="datetime-local"
                  value={startAt}
                  onChange={(ev) => setStartAt(ev.target.value)}
                  className="border-line bg-field text-foreground num rounded-lg"
                />
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Label htmlFor="schedule-stop" className="micro-label">
                Stop at
              </Label>
              <Input
                id="schedule-stop"
                type="datetime-local"
                value={stopAt}
                onChange={(ev) => setStopAt(ev.target.value)}
                className="border-line bg-field text-foreground num rounded-lg"
              />
            </div>
          </div>

          {isDraft && <PlannedRunHint experiment={experiment} startAt={start} stopAt={toEpoch(stopAt)} />}

          {/*
            The switch sits immediately before its label rather than pushed to the far edge
            by justify-between. Across a full-width card that put the only control in this
            block a long way from the words explaining it, which made it easy to miss
            entirely — and this is the one setting here that changes whether the engine acts
            on its own.
          */}
          <div className="border-line bg-panel2 rounded-card border p-3">
            <div className="flex items-center gap-3">
              <Switch id="schedule-significance" checked={stopOnSignificance} onCheckedChange={setStopOnSignificance} />
              <Label htmlFor="schedule-significance" className="text-foreground cursor-pointer text-meta">
                Stop early if the result is conclusive
              </Label>
            </div>
            {/*
              The honest version of what this does. It is not "stop when significant":
              it needs SPRT and the corrected p-value to agree, at least a fortnight of
              traffic, and a healthy split. A small true effect will never satisfy all
              three, which is why the stop time above is required rather than optional.
            */}
            <p className="supporting mt-2">
              Requires at least two weeks of traffic, a healthy sample ratio, and two independent readings agreeing. If
              they never agree, the stop time above still ends it.
            </p>
          </div>

          {stopOnSignificance && !stopAt && (
            <p className="text-warn font-mono text-micro">A stop time is required as a backstop.</p>
          )}

          {weightsUnavailable && (
            <p className="text-sm text-amber-600 dark:text-amber-400">
              {variants
                ? 'Variant weights must sum to 100% before a start time can be scheduled.'
                : 'Loading variant weights…'}
            </p>
          )}

          <div className="flex items-center gap-2">
            <Button
              type="submit"
              disabled={isSaving || weightsUnavailable || (!startAt && !stopAt && !stopOnSignificance)}
              className="bg-cta hover:bg-ctahover h-8 rounded-lg px-3 text-meta text-white"
            >
              {isSaving ? 'Saving…' : 'Save schedule'}
            </Button>
            {schedule && (
              <Button
                type="button"
                variant="ghost"
                onClick={onRemove}
                className="text-dim hover:text-foreground hover:bg-hover h-8 rounded-lg px-3 text-meta"
              >
                Remove
              </Button>
            )}
            {schedule?.stopAt && (
              <span className="log-line text-dim ml-auto">
                Ends {new Date(schedule.stopAt).toLocaleString()}
                {schedule.startAt && schedule.stopAt - schedule.startAt < 14 * DAY && ' — under two weeks'}
              </span>
            )}
          </div>
        </form>
      </div>
    </section>
  );
};
